/**
 * First-touch acquisition capture (Bucket B).
 *
 * On web first load we stash referrer / user-agent / UTM / landing into storage
 * (once — first touch wins). Once the user is authenticated near signup we send
 * it to /attribution/capture, which itself INSERTs-once per cognito_id. Native
 * has no referrer, so it records just its platform. Best-effort throughout.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiPost } from './apiClient';
import { getSessionIds } from '../store/actions/_sessionIds';
import { getPlatformDevice } from './devicePlatform';
import { isWeb } from './platform';

const FIRST_TOUCH_KEY = '@eleplan/first_touch';
const SENT_KEY = '@eleplan/attribution_sent';

export interface FirstTouch {
  platform: string;
  referrer?: string | null;
  landing_url?: string | null;
  user_agent?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
  /** Marketing-site cookieless visitor id, arriving as ?eleplan_vid= on the app
   *  link. The stitch key that joins marketing_events to this signup. */
  visitor_id?: string | null;
}

/** Read acquisition signals available on the current surface (web only for the
 *  rich fields; native just reports its platform). Never throws. */
export function readFirstTouch(): FirstTouch {
  const ft: FirstTouch = { platform: getPlatformDevice() };
  try {
    if (isWeb && typeof document !== 'undefined') ft.referrer = document.referrer || null;
    if (isWeb && typeof navigator !== 'undefined') ft.user_agent = navigator.userAgent || null;
    if (isWeb && typeof window !== 'undefined' && window.location) {
      ft.landing_url = window.location.href || null;
      const q = new URLSearchParams(window.location.search || '');
      ft.utm_source = q.get('utm_source');
      ft.utm_medium = q.get('utm_medium');
      ft.utm_campaign = q.get('utm_campaign');
      ft.utm_term = q.get('utm_term');
      ft.utm_content = q.get('utm_content');
      ft.visitor_id = q.get('eleplan_vid');
    }
  } catch {
    /* ignore — best-effort */
  }
  return ft;
}

/** Stash first-touch the first time the app loads; no-op once captured. */
export async function captureFirstTouch(): Promise<void> {
  try {
    const existing = await AsyncStorage.getItem(FIRST_TOUCH_KEY);
    if (existing) return;
    await AsyncStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(readFirstTouch()));
  } catch {
    /* best-effort */
  }
}

/** Once authenticated, send the stored first-touch a single time per device.
 *  The backend INSERTs-once per cognito_id, so re-sends are harmless. */
export async function sendAttributionIfNeeded(): Promise<void> {
  try {
    if (await AsyncStorage.getItem(SENT_KEY)) return;
    const { accountId, cognitoId } = getSessionIds();
    if (!accountId || !cognitoId) return;
    const raw = await AsyncStorage.getItem(FIRST_TOUCH_KEY);
    const ft: FirstTouch = raw ? JSON.parse(raw) : readFirstTouch();
    const res = await apiPost(`/attribution/capture/${accountId}/${cognitoId}`, ft);
    if (res?.success !== false) await AsyncStorage.setItem(SENT_KEY, '1');
  } catch {
    /* best-effort */
  }
}
